import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSlideCopy, useUserMarket } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import { resolveSite } from '@/lib/site'
import type { SlideProps } from '@/components/SlideWizard'
import {
  getReportingChannels,
  getMarketLabel,
  type ReportingChannel,
} from '@/lib/emergency-contacts'
import ftcSeal from '@/assets/ftc-seal.jpg'
import { copy, type ReportChannel, type ReportLink } from './Slide7Report.copy'

const ease = [0.16, 1, 0.3, 1] as const

// Per-org logo mapping. Falls back to the violet external-link icon when
// no logo is set for a given org. Never used on the Qatar face: the FTC
// seal carries baked-in US text.
const ORG_LOGOS: Record<string, string> = {
  FTC: ftcSeal,
}

/**
 * Splits a channel's display `detail` into the URL part and phone part.
 * Returns whichever pieces are present. The detail uses " • " as a
 * separator when both are bundled (e.g. "actionfraud.police.uk • 0300 123 2040").
 */
function splitDetail(channel: ReportingChannel): { urlText?: string; phoneText?: string } {
  const parts = channel.detail.split(' • ')
  if (channel.url && channel.phone && parts.length === 2) {
    return { urlText: parts[0], phoneText: parts[1] }
  }
  if (channel.url) return { urlText: channel.detail }
  if (channel.phone) return { phoneText: channel.detail }
  return {}
}

/** Shared market data, normalised to the copy file's channel shape. */
function fromShared(channel: ReportingChannel): ReportChannel {
  const { urlText, phoneText } = splitDetail(channel)
  const links: ReportLink[] = []
  if (urlText && channel.url) links.push({ text: urlText, href: channel.url, kind: 'url' })
  if (phoneText && channel.phone) {
    links.push({ text: phoneText, href: `tel:${channel.phone}`, kind: 'phone' })
  }
  return { org: channel.org, detail: channel.detail, whenToUse: channel.whenToUse, links }
}

function ExternalLinkIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
    </svg>
  )
}

function ShieldCheckIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317-4.66-1.647-8-6.092-8-11.317 0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function ChannelLinks({
  channel,
  variant,
}: {
  channel: ReportChannel
  variant: 'hero' | 'list'
}) {
  const isHero = variant === 'hero'

  if (channel.links.length === 0) {
    return (
      <p
        className={
          isHero
            ? 'mt-1 text-base text-slate-600 leading-snug'
            : 'mt-0.5 text-sm text-slate-600'
        }
      >
        {channel.detail}
      </p>
    )
  }

  if (isHero) {
    return (
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {channel.links.map((link) =>
          link.kind === 'url' ? (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className="text-base sm:text-lg font-medium text-slate-900 underline decoration-violet-400 hover:decoration-violet-600 underline-offset-4 transition-colors break-all"
            >
              {link.text}
            </a>
          ) : (
            <a
              key={link.href}
              href={link.href}
              className="text-base sm:text-lg font-medium text-slate-900 underline decoration-violet-400 hover:decoration-violet-600 underline-offset-4 transition-colors tabular-nums"
            >
              {link.text}
            </a>
          ),
        )}
      </div>
    )
  }

  // list variant (slideover)
  const linkClass =
    'block text-sm text-slate-700 hover:text-violet-700 underline decoration-slate-300 underline-offset-4 transition-colors tabular-nums break-words'
  return (
    <div className="mt-1 space-y-0.5">
      {channel.links.map((link) =>
        link.kind === 'url' ? (
          <a key={link.href} href={link.href} target="_blank" rel="noreferrer" className={linkClass}>
            {link.text}
          </a>
        ) : (
          <a key={link.href} href={link.href} className={linkClass}>
            {link.text}
          </a>
        ),
      )}
    </div>
  )
}

export default function Slide7Report(_props: SlideProps) {
  const userMarket = useUserMarket()
  const c = useSlideCopy(copy)
  const isQatar = resolveSite() === 'qatar'
  // The slide-over enters from the end edge of the reading direction.
  const rtl = useLocale() === 'ar'
  const offscreen = rtl ? '-100%' : '100%'
  const channels: ReportChannel[] =
    c.channels ?? getReportingChannels(userMarket).map(fromShared)
  const marketLabel = c.marketLabel ?? getMarketLabel(userMarket)
  const primary = channels[0]
  const primaryLogo = isQatar ? undefined : ORG_LOGOS[primary.org]
  const [guideOpen, setGuideOpen] = useState(false)

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 mx-auto max-w-3xl lg:max-w-5xl">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease }}
        className="text-xs sm:text-sm uppercase tracking-[0.25em] text-slate-400 font-semibold"
      >
        {c.eyebrow}
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.25, ease }}
        className="mt-4 text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.1]"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.55, ease }}
        className="mt-8 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-2xl"
      >
        {c.body}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8, ease }}
        className="mt-10 w-full max-w-4xl rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4 sm:px-6 sm:py-5 text-start shadow-[0_4px_16px_rgba(15,23,42,0.04)]"
      >
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 sm:gap-x-6">
          {/* Icon (logo when available, otherwise violet external-link) */}
          {primaryLogo ? (
            <span className="inline-flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-slate-200 overflow-hidden">
              <img
                src={primaryLogo}
                alt={c.logoAlt.replace('{org}', primary.org)}
                className="h-full w-full object-contain"
              />
            </span>
          ) : (
            <span className="inline-flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-600">
              <ExternalLinkIcon className="dir-flip h-5 w-5 sm:h-6 sm:w-6" />
            </span>
          )}

          {/* Org name */}
          <p className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight shrink-0">
            {primary.org}
          </p>

          <div className="hidden sm:block h-8 w-px bg-slate-300/80 shrink-0" aria-hidden />

          {/* URL/phone link + market chip */}
          <div className="flex items-center gap-3 shrink-0">
            <ChannelLinks channel={primary} variant="hero" />
            <span className="inline-flex items-center rounded-full bg-slate-200/80 px-2.5 py-0.5 text-sm font-medium text-slate-600 shrink-0">
              {marketLabel}
            </span>
          </div>

          <div className="hidden lg:block h-8 w-px bg-slate-300/80 shrink-0" aria-hidden />

          {/* Description */}
          <p className="basis-full text-base text-slate-600 leading-snug min-w-0 lg:basis-0 lg:flex-1">
            {primary.whenToUse}
          </p>
        </div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.0, ease }}
        onClick={() => setGuideOpen(true)}
        className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 cursor-pointer"
      >
        {c.buttonLabel}
        <svg className="dir-flip h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </motion.button>

      <AnimatePresence>
        {guideOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setGuideOpen(false)}
              className="fixed inset-0 z-40 bg-slate-900/40"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 w-full max-w-md bg-white shadow-2xl overflow-y-auto text-start"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/90 backdrop-blur px-6 py-4">
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-violet-600">
                    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  <h3 className="text-sm font-semibold text-slate-700">
                    {c.guideHeading.replace('{market}', marketLabel)}
                  </h3>
                </div>
                <button
                  onClick={() => setGuideOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700 cursor-pointer"
                  aria-label={c.closeLabel}
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-7 sm:px-8">
                <p className="text-base text-slate-600 leading-relaxed">
                  {c.introLine}
                </p>

                <h4 className="mt-7 text-base font-semibold text-slate-900">
                  {c.haveTheseHeading}
                </h4>
                <ul className="mt-2 space-y-1.5 text-base text-slate-600 leading-relaxed">
                  {[c.have1, c.have2, c.have3, c.have4].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1.5 inline-flex h-1 w-1 shrink-0 rounded-full bg-slate-300" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {c.policeNote}
                  </p>
                </div>

                <h4 className="mt-6 text-base font-semibold text-slate-900">
                  {c.whereToReportPrefix} {marketLabel}
                </h4>
                <div className="mt-3 space-y-3">
                  {channels.map((ch) => (
                    <div
                      key={ch.org}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3"
                    >
                      <p className="text-base font-semibold text-slate-900">
                        {ch.org}
                      </p>
                      <ChannelLinks channel={ch} variant="list" />
                      <p className="mt-1.5 text-sm text-slate-500 leading-snug">
                        {ch.whenToUse}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-xl bg-violet-50 px-4 py-3.5">
                  <div className="flex items-start gap-2.5">
                    <span className="shrink-0 mt-0.5 text-violet-500">
                      <ShieldCheckIcon className="h-4 w-4" />
                    </span>
                    <p className="text-base text-slate-700 leading-relaxed">
                      {c.closingLine}
                    </p>
                  </div>
                </div>

                <h4 className="mt-7 text-base font-semibold text-slate-900">
                  {c.whatHappensNextHeading}
                </h4>
                <p className="mt-1 text-base text-slate-600 leading-relaxed">
                  {c.whatHappensNextBody}
                </p>
                <p className="mt-3 text-base text-slate-600 leading-relaxed">
                  {c.documentationLine}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
